import * as stylex from '@stylexjs/stylex';
import { useMutation, useQuery, useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { DialogTrigger } from 'react-aria-components';
import { object as vObject, string as vString, optional as vOptional } from 'valibot';

import { ArticleListItem } from '@/components/article-list-item.component';
import { ArticleList } from '@/components/article-list.component';
import { InterestsDialog } from '@/components/interests-dialog.component';
import { InterestTabs } from '@/components/interests-tabs.component';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger.hook';
import {
  Paragraph,
  ProgressIndicator,
  Skeleton,
  Tab,
  Button,
  Heading,
  EmptyState
} from '@/libs/serenity-ui';
import { articleMutations, articleQueries } from '@/resources/article/article.api';
import { userMutations, userQueries } from '@/resources/user/user.api';

export const Route = createFileRoute('/_app/_list/')({
  component: RouteComponent,
  validateSearch: vObject({
    interest: vOptional(vString()),
  }),
  staticData: {
    contextBarComponent: ContextBarComponent,
    contextBarPendingComponent: ContextBarPendingState,
    pendingDescription: 'We’re gathering the best reads for you...',
  },
});

function RouteComponent() {
  const search = Route.useSearch();

  const userInterestsQuery = useQuery(userQueries.interests);

  const saveArticleMutation = useMutation(articleMutations.save({ feed: { interest: search.interest } }));
  const unsaveArticleMutation = useMutation(articleMutations.unsave({ feed: { interest: search.interest } }));
  const excludeArticleMutation = useMutation(articleMutations.exclude({ feed: { interest: search.interest } }));
  const undoExcludeArticleMutation = useMutation(articleMutations.removeExclude({ feed: { interest: search.interest } }));
  const updateUserInterestsMutation = useMutation(userMutations.updateInterests);

  const {
    data,
    fetchNextPage,
    isLoading,
    isFetching,
    hasNextPage
  } = useSuspenseInfiniteQuery(articleQueries.feed({ interest: search.interest }));

  useInfiniteScrollTrigger({ isFetching, hasNextPage, fetchNextPage });

  const items = data?.pages?.flatMap((page) => page.articles);

  return items.length > 0 ? (
    <>
      <ArticleList aria-label="Feed list" items={items}>
        {({ key, ref, index, style, ...item }) => (
          <ArticleListItem
            authors={item.authors}
            data-index={index}
            id={item.id}
            isExcluded={item.excluded}
            isLast={index === items.length - 1}
            isSaved={item.saved}
            isSeen={item.seen}
            key={key}
            publicationDate={item.publication_date}
            publicationId={item.publication_id}
            publicationName={item.publication_name}
            ref={ref}
            style={style}
            title={item.title}
            url={item.url}
            onExcludePress={() => excludeArticleMutation.mutate({ article_id: item.id })}
            onSavePress={() => saveArticleMutation.mutate({ article_id: item.id })}
            onUndoExcludePress={() => undoExcludeArticleMutation.mutate({ article_id: item.id })}
            onUnsavePress={() => unsaveArticleMutation.mutate({ article_id: item.id })}
          />
        )}
      </ArticleList>
      {!isLoading && hasNextPage && <ProgressIndicator size={24} style={progressIndicatorStyles.base} />}
    </>
  ) : (
    <EmptyState style={emptyStateStyles.base}>
      <Heading level={1} size={52}>No articles yet</Heading>
      <Paragraph>Add interests to get personalized recommendations</Paragraph>
      <DialogTrigger>
        <Button isRounded size={40} style={emptyStateStyles.button}>Choose interests</Button>
        <InterestsDialog
          defaultValue={userInterestsQuery.data}
          isEdit
          onSubmit={(value) => updateUserInterestsMutation.mutateAsync({ interest_ids: value })}
        />
      </DialogTrigger>
    </EmptyState>
  );
}

function ContextBarComponent() {
  const routeContext = Route.useRouteContext();
  const search = Route.useSearch();

  const userInterestsQuery = useSuspenseQuery(userQueries.interests);

  const tabs = [{ name: 'For you', id: -1, tree_number: '' }, ...(userInterestsQuery.data ?? [])];

  return (
    <InterestTabs
      items={tabs}
      selectedKey={search.interest ?? -1}>
      {(item) =>
        <Tab
          href={item.tree_number ? `/?interest=${item.tree_number}` : '/'}
          id={item.tree_number || item.id}
          onHoverStart={() => routeContext.queryClient.ensureInfiniteQueryData(articleQueries.feed({ interest: item.tree_number }))}>
          {item.name}
        </Tab>}
    </InterestTabs>
  );
}

function ContextBarPendingState() {
  return (
    <InterestTabs isDisabled>
      <Tab>For you</Tab>
      <Tab><Skeleton style={skeletonStyles.base} /></Tab>
      <Tab><Skeleton style={skeletonStyles.base} /></Tab>
      <Tab><Skeleton style={[skeletonStyles.base, skeletonStyles.long]} /></Tab>
    </InterestTabs>
  );
}

const progressIndicatorStyles = stylex.create({
  base: {
    marginBottom: 20,
    marginTop: 56,
  }
});

const skeletonStyles = stylex.create({
  base: {
    height: 20,
    width: 77,
  },
  long: {
    width: 236
  },
});

const emptyStateStyles = stylex.create({
  base: {
    paddingTop: 140,
  },
  button: {
    marginTop: 24,
  }
});
